import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { config } from '../../utils/config';
import AccountLayout from '../../components/Account/AccountLayout';
import './AccountPricingPage.css';

interface Product {
  id: string;
  name: string;
  description: string;
  type: 'one_time';
  credits: number;
  price_cad: number;
  stripe_price_id: string;
}

interface AccountData {
  credits_balance: number;
  credits_total_purchased?: number;
  credits_total_used?: number;
  last_credit_activity?: string | null;
}

const AccountPricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [accountData, setAccountData] = useState<AccountData | null>({
    credits_balance: 0,
    credits_total_purchased: 0,
    credits_total_used: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [processingPurchase, setProcessingPurchase] = useState<string | null>(null);

  // Format plan names for better display
  const getFormattedPlanName = (name: string): string => {
    if (name.includes('ZoningPal Report')) {
      return 'Single Report Purchase';
    }
    return name;
  };

  // Helper function for button text
  const getButtonText = (product: Product, isAuthenticated: boolean) => {
    if (!isAuthenticated) {
      return 'Get Started';
    }
    return 'Purchase Credits';
  };

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch products (public - no auth required)
  const loadProducts = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/payments-simple/products`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load products');
      }

      const data = await response.json();
      setProducts(data.products);
    } catch (error: any) {
      console.error('Error loading products:', error);
      setError(error.message || 'Failed to load pricing information');
    }
  };

  // Load account data (only if authenticated) - non-blocking
  const loadAccountData = async () => {
    if (!user || !session) {
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}/api/payments-simple/account`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Don't show error for account data, just use default values
        console.warn('Account data not available, using defaults');
        setAccountData({
          credits_balance: 0,
          credits_total_purchased: 0,
          credits_total_used: 0
        });
        return;
      }

      const data = await response.json();
      setAccountData(data.account);
    } catch (error: any) {
      console.warn('Error loading account data, using defaults:', error);
      // Don't set error for account data, just use default values
      setAccountData({
        credits_balance: 0,
        credits_total_purchased: 0,
        credits_total_used: 0
      });
    }
  };

  // Load data on component mount - simplified
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load products first (required for display)
        await loadProducts();
        // Load account data in background (optional)
        loadAccountData();
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, session]);

  // Handle purchase
  const handlePurchase = async (product: Product) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setProcessingPurchase(product.id);
    setError(null);
    setSuccessMessage(null);

    try {
      // Create checkout session for credit purchase
      const response = await fetch(`${config.API_URL}/api/payments-simple/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          productId: product.id,
          priceId: product.stripe_price_id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication required. Please sign in again.');
        } else {
          setError(data.error || 'Failed to create checkout session');
        }
        setProcessingPurchase(null);
        return;
      }

      if (data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Purchase error:', error);
      setError('Purchase failed. Please try again.');
      setProcessingPurchase(null);
    }
  };

  if (loading) {
    return (
      <AccountLayout activeSection="pricing">
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
        }}>
          <div style={{ 
            fontSize: '14px', 
            color: '#64748b' 
          }}>
            Loading pricing information...
          </div>
        </div>
      </AccountLayout>
    );
  }

  // Filter to only show one-time products
  const oneTimeProducts = products.filter(product => product.type === 'one_time');

  if (!user) {
    return (
      <AccountLayout activeSection="pricing">
        <div className="account-pricing-auth-required">
          <div className="account-pricing-auth-content">
            <h1>Purchase Credits</h1>
            <p>Please sign in to access pricing and purchase credits.</p>
            <button onClick={() => navigate('/login')} className="btn-primary">
              Sign In
            </button>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout activeSection="pricing">
      <div className="account-pricing-page">
        <div className="account-pricing-container">
          <div className="account-pricing-header">
          <h1>Purchase Credits</h1>
          <p className="account-pricing-subtitle">
            Buy credits to generate zoning analysis reports. No subscriptions, pay as you go.
          </p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <div className="account-pricing-content">
          {/* Current Credits */}
          <div className="current-plan-section">
            <h3>Current Credits: <span className="plan-name">{accountData?.credits_balance || 0} credits available</span></h3>
            <p className="credits-remaining">Each analysis report costs 1 credit</p>
          </div>

          {/* Pricing Grid - Same as main pricing page */}
          <div className="account-pricing-grid">
            {/* Credit Purchase Card */}
            {oneTimeProducts.length > 0 && oneTimeProducts.map(product => (
              <div key={product.id} className="account-pricing-card">
                <div className="account-pricing-card-header">
                  <h3 className="account-pricing-card-title">Single Report Purchase</h3>
                  <div className="account-pricing-card-price">$49</div>
                  <p className="account-pricing-card-period">one-time</p>
                  <div className="pricing-note">
                    <span className="pricing-asterisk">*</span>
                    <span className="pricing-note-text">Limited post-launch price - get it while it lasts!</span>
                  </div>
                </div>

                <div className="account-pricing-card-content">
                  <p className="account-pricing-card-description">
                    {product.description}
                  </p>
                  
                  <ul className="account-pricing-card-features">
                    <li>✓ {product.credits || 1} credit for immediate use</li>
                    <li>✓ No monthly commitment</li>
                    <li>✓ Perfect for one-time needs</li>
                    <li>✓ Full zoning analysis</li>
                    <li>✓ Professional PDF report</li>
                  </ul>
                </div>
                
                <div className="account-pricing-card-footer">
                  <button 
                    className={`account-pricing-card-button ${processingPurchase === product.id ? 'loading' : ''}`}
                    onClick={() => handlePurchase(product)}
                    disabled={processingPurchase === product.id}
                  >
                    {processingPurchase === product.id ? 'Processing...' : 'Purchase Credits'}
                  </button>
                </div>
              </div>
            ))}
            
            {/* Enterprise Card */}
            <div className="account-pricing-card enterprise-card">
              <div className="account-pricing-card-header">
                <h3 className="account-pricing-card-title">Enterprise Solutions</h3>
                <div className="account-pricing-card-price">Custom</div>
                <p className="account-pricing-card-period">Contact for pricing</p>
              </div>

              <div className="account-pricing-card-content">
                <p className="account-pricing-card-description">
                  Custom pricing for large-scale needs and enterprise requirements
                </p>
                
                <ul className="account-pricing-card-features">
                  <li>✓ Bulk analysis capabilities</li>
                  <li>✓ Custom integration options</li>
                  <li>✓ Priority support</li>
                  <li>✓ Volume discounts</li>
                  <li>✓ Dedicated account management</li>
                </ul>
              </div>
              
              <div className="account-pricing-card-footer">
                <a 
                  href="mailto:sales@zoningpal.com?subject=Enterprise Pricing Inquiry" 
                  className="account-pricing-card-button"
                >
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
        </div>

          <div className="account-pricing-footer">
            <p>All prices in CAD. Secure payment processing by Stripe.</p>
          </div>
        </div>
      </div>
    </AccountLayout>
  );
};

export default AccountPricingPage;
